import {
  Auth,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  type User as FirebaseUser
} from 'firebase/auth';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import UserController from '../apis/userController';
import { UserType, UserTypeEnum } from '../constants';
import { UserInfoResponse } from '../type/response/user';
import {
  getSessionItem,
  removeSessionItem,
  SessionStorageKeys,
  setSessionItem
} from '../utils/sessionStorage';

export type User = Pick<
  UserInfoResponse,
  | 'userId'
  | 'email'
  | 'displayName'
  | 'role'
  | 'userGroup'
  | 'userType'
  | 'isKKT' // user.userType
  | 'isAgency' // user.userType
  | 'isSales' // user.userType
  | 'isIndividual' // user.userType
  | 'isAdmin' // user.role
  | 'isApplicant' // user.role
  | 'agentId' // isAgency, isSales, isIndividual が true の場合は必須
  | 'agentGroupId' // isSales が true の場合は必須
  | 'contractType' // isAgency, isSales, isIndividual が true の場合は必須
  | 'stationId' // isAdmin が true の場合は必須
  | 'stationName' // isAdmin が true の場合は必須
>;

type AuthContextType = {
  user: User | null;
  idToken: string | null;
  login: (email: string, password: string, stationId?: string) => Promise<void>;
  logout: () => Promise<void>;
  isInitializing: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const userController = new UserController();

interface AuthProviderProps {
  children: ReactNode;
  firebaseAuth: Auth;
  userType: UserType;
  onUserLoaded?: (user: UserInfoResponse | null) => void;
}

export const AuthProvider = ({
  children,
  firebaseAuth,
  userType,
  onUserLoaded
}: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const revision = useRef(0);
  const signingIn = useRef(false);
  const restoredUid = useRef<string | null>(null);

  const clearSession = useCallback(() => {
    restoredUid.current = null;
    removeSessionItem(SessionStorageKeys.USER);
    removeSessionItem(SessionStorageKeys.ID_TOKEN);
    setUser(null);
    setIdToken(null);
    onUserLoaded?.(null);
  }, [onUserLoaded]);

  const restoreUser = useCallback(
    async (firebaseUser: FirebaseUser, loginStationId?: string) => {
      const request = ++revision.current;
      const isCurrent = () =>
        request === revision.current &&
        firebaseAuth.currentUser === firebaseUser;
      // 権限情報はキャッシュを信用せず、Firebase の認証後に API から取得する。
      const cachedUser = getSessionItem<User>(SessionStorageKeys.USER);
      setIsInitializing(true);
      clearSession();
      try {
        const token = await firebaseUser.getIdToken();
        if (!isCurrent()) return;
        setSessionItem(SessionStorageKeys.ID_TOKEN, token);
        setIdToken(token);

        let stationId = loginStationId;
        const stationKey = `loginStation:${firebaseAuth.app.options.projectId}:${firebaseUser.tenantId ?? ''}:${userType}:${firebaseUser.uid}`;
        if (userType === UserTypeEnum.KKT && !stationId) {
          try {
            stationId = localStorage.getItem(stationKey) || undefined;
          } catch {
            // ストレージが利用できない場合は、このタブの駅情報を使用する。
          }
          if (
            !stationId &&
            cachedUser?.email === firebaseUser.email &&
            cachedUser?.isKKT
          ) {
            stationId = cachedUser.stationId;
          }
          // 駅未選択の場合はログイン画面で選択してもらう。
          if (!stationId) {
            clearSession();
            return;
          }
        }

        const info = await userController.getUserInfo(
          userType,
          stationId ? { stationId } : undefined
        );
        if (!isCurrent()) return;
        if (!info) throw new Error('invalidUserInfo');
        if (userType === UserTypeEnum.KKT && stationId) {
          try {
            localStorage.setItem(stationKey, info.stationId || stationId);
          } catch {
            // 保存できなくても、このタブでのログインは継続できる。
          }
        }
        setSessionItem(SessionStorageKeys.USER, info);
        removeSessionItem('forced-logout-handled');
        restoredUid.current = firebaseUser.uid;
        onUserLoaded?.(info);
        setUser(info);
      } catch (error) {
        if (isCurrent()) clearSession();
        throw error;
      } finally {
        if (isCurrent()) setIsInitializing(false);
      }
    },
    [firebaseAuth, userType, clearSession, onUserLoaded]
  );

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(firebaseAuth, async firebaseUser => {
      if (!firebaseUser) {
        ++revision.current;
        clearSession();
        setIsInitializing(false);
        return;
      }
      // 明示的なログイン中は、選択した駅を渡す login 側で復元を完了する。
      if (signingIn.current) return;
      try {
        if (restoredUid.current === firebaseUser.uid) {
          const request = revision.current;
          const token = await firebaseUser.getIdToken();
          if (
            request !== revision.current ||
            firebaseAuth.currentUser !== firebaseUser
          )
            return;
          setSessionItem(SessionStorageKeys.ID_TOKEN, token);
          setIdToken(token);
        } else {
          await restoreUser(firebaseUser);
        }
      } catch (error) {
        console.error('[AuthContext] Session restoration failed:', error);
      }
    });
    return () => {
      ++revision.current;
      unsubscribe();
    };
  }, [firebaseAuth, restoreUser, clearSession]);

  const login = useCallback(
    async (email: string, password: string, stationId?: string) => {
      signingIn.current = true;
      ++revision.current;
      clearSession();
      try {
        const credential = await signInWithEmailAndPassword(
          firebaseAuth,
          email,
          password
        );
        await restoreUser(credential.user, stationId);
      } finally {
        signingIn.current = false;
        setIsInitializing(false);
      }
    },
    [firebaseAuth, restoreUser, clearSession]
  );

  const logout = useCallback(async () => {
    ++revision.current;
    clearSession();
    setIsInitializing(false);
    try {
      await firebaseAuth.signOut();
    } catch (error) {
      console.error('[AuthContext] Firebase signOut failed:', error);
    }
  }, [firebaseAuth, clearSession]);

  useEffect(() => {
    const handleForcedLogout = () => {
      void logout();
    };
    window.addEventListener('app:force-logout', handleForcedLogout);
    return () =>
      window.removeEventListener('app:force-logout', handleForcedLogout);
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{ user, idToken, login, logout, isInitializing }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
